/**
 * WebSocket transport – mesajları NotificationService'e iletir.
 * Çoklu sekmede yalnızca lider sekme WS açar; gelen mesajlar BroadcastChannel ile paylaşılır.
 */
import { handleIncomingNotification } from '../services/notificationService';
import { isWsLeaderTab, broadcastNotification } from './syncBroadcast';

const listeners = new Set();
const RECONNECT_BASE_MS = 2_000;
const RECONNECT_MAX_MS = 60_000;

let ws = null;
let reconnectAttempt = 0;
let reconnectTimer = null;
let intentionalClose = false;

function buildWsUrl() {
  const base = process.env.REACT_APP_BACKEND_SERVER || '';
  if (!base) return null;
  let wsBase = base.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
  if (!wsBase.endsWith('/')) wsBase += '/';
  const token = localStorage.getItem('jwt_token') || '';
  // nginx prod: /api/ws/ → backend /ws/ — /api/ prefix korunmalı
  return `${wsBase}ws/notifications?token=${encodeURIComponent(token)}`;
}

function emitTransport(event) {
  for (const fn of listeners) {
    try { fn(event); } catch (_) { /* ignore */ }
  }
}

function scheduleReconnect() {
  if (intentionalClose || reconnectTimer) return;
  const delay = Math.min(RECONNECT_BASE_MS * (2 ** reconnectAttempt), RECONNECT_MAX_MS);
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connectNotificationSocket();
  }, delay);
}

function handleMessage(raw) {
  let msg;
  try {
    msg = JSON.parse(raw);
  } catch {
    return;
  }
  emitTransport(msg);
  handleIncomingNotification(msg);
  broadcastNotification(msg);
}

export function subscribeNotificationSocket(handler) {
  if (typeof handler !== 'function') return () => {};
  listeners.add(handler);
  return () => listeners.delete(handler);
}

export function connectNotificationSocket() {
  if (!isWsLeaderTab()) return;

  const url = buildWsUrl();
  if (!url || !localStorage.getItem('jwt_token')) return;

  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  intentionalClose = false;
  try {
    ws = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }

  ws.onopen = () => {
    reconnectAttempt = 0;
    emitTransport({ type: 'WS_OPEN' });
  };

  ws.onmessage = (evt) => handleMessage(evt.data);

  ws.onclose = () => {
    ws = null;
    emitTransport({ type: 'WS_CLOSE' });
    scheduleReconnect();
  };

  ws.onerror = () => {
    try { ws?.close(); } catch (_) { /* ignore */ }
  };
}

export function disconnectNotificationSocket() {
  intentionalClose = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectAttempt = 0;
  if (ws) {
    try { ws.close(); } catch (_) { /* ignore */ }
    ws = null;
  }
}

export function getNotificationSocketState() {
  if (!ws) return 'closed';
  if (ws.readyState === WebSocket.OPEN) return 'open';
  if (ws.readyState === WebSocket.CONNECTING) return 'connecting';
  return 'closing';
}
